import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { Copy, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

interface StripeEvent {
  id: string;
  type: string;
  created_at: string;
}

interface OrderCredit {
  id: string;
  user_id: string;
  delta_resume: number;
  delta_letter: number;
  reason: string;
  created_at: string;
}

const AdminDiagnostics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [stripeEvents, setStripeEvents] = useState<StripeEvent[]>([]);
  const [orderCredits, setOrderCredits] = useState<OrderCredit[]>([]);
  
  const webhookUrl = `${window.location.origin.replace('5173', '54321')}/functions/v1/stripe-webhook`;

  useEffect(() => {
    checkAdminAndLoadData();
  }, []);

  const checkAdminAndLoadData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate("/login");
        return;
      }

      // Check if user is admin
      const { data: adminData } = await supabase
        .from("admin_users")
        .select("user_id")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!adminData) {
        toast.error("Access denied: Admin privileges required");
        navigate("/app");
        return;
      }

      setIsAdmin(true);
      await loadDiagnostics();
    } catch (error) {
      console.error("Error checking admin status:", error);
      toast.error("Failed to verify admin access");
      navigate("/app");
    } finally {
      setLoading(false);
    }
  };

  const loadDiagnostics = async () => {
    try {
      // Load last 10 stripe events
      const { data: events } = await supabase
        .from("stripe_events")
        .select("id, type, created_at")
        .order("created_at", { ascending: false })
        .limit(10);

      if (events) setStripeEvents(events);

      // Load last 10 credit grants
      const { data: credits } = await supabase
        .from("order_credits")
        .select("id, user_id, delta_resume, delta_letter, reason, created_at")
        .order("created_at", { ascending: false })
        .limit(10);

      if (credits) setOrderCredits(credits);
    } catch (error) {
      console.error("Error loading diagnostics:", error);
      toast.error("Failed to load diagnostics data");
    }
  };

  const copyWebhookUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    toast.success("Webhook URL copied to clipboard");
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <RefreshCw className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="container mx-auto max-w-6xl">
        <div className="mb-8">
          <h1 className="mb-2 text-4xl font-bold">Admin Diagnostics</h1>
          <p className="text-muted-foreground">Stripe webhook health and credit grant monitoring</p>
        </div>

        {/* Webhook Configuration */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Webhook Configuration</CardTitle>
            <CardDescription>Configure this URL in your Stripe Dashboard</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded bg-muted p-3 text-sm">{webhookUrl}</code>
                <Button onClick={copyWebhookUrl} variant="outline" size="icon">
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <div className="rounded-lg border border-primary/20 bg-primary/5 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-primary" />
                  <h4 className="font-semibold">Required Events</h4>
                </div>
                <ul className="ml-7 list-disc space-y-1 text-sm text-muted-foreground">
                  <li>checkout.session.completed</li>
                  <li>invoice.paid</li>
                  <li>customer.subscription.updated</li>
                  <li>charge.refunded</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Webhook Events */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Recent Webhook Events</CardTitle>
                <CardDescription>Last 10 processed Stripe events</CardDescription>
              </div>
              <Button onClick={loadDiagnostics} variant="outline" size="sm">
                <RefreshCw className="mr-2 h-4 w-4" />
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {stripeEvents.length === 0 ? (
              <div className="rounded-lg border-2 border-dashed p-8 text-center">
                <AlertCircle className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">
                  No webhook events received yet. Check your Stripe webhook configuration.
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {stripeEvents.map((event) => (
                  <div key={event.id} className="flex items-center justify-between rounded-lg border p-3">
                    <div className="flex items-center gap-3">
                      <Badge variant="outline">{event.type}</Badge>
                      <code className="text-xs text-muted-foreground">{event.id}</code>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {new Date(event.created_at).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Credit Grants */}
        <Card>
          <CardHeader>
            <CardTitle>Credit Grants History</CardTitle>
            <CardDescription>Recent credit additions to user accounts</CardDescription>
          </CardHeader>
          <CardContent>
            {orderCredits.length === 0 ? (
              <div className="rounded-lg border-2 border-dashed p-8 text-center">
                <AlertCircle className="mx-auto mb-2 h-8 w-8 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">No credits granted yet.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {orderCredits.map((credit) => (
                  <div key={credit.id} className="flex items-center justify-between rounded-lg border p-3">
                    <div className="flex items-center gap-3">
                      <Badge>{credit.reason}</Badge>
                      <span className="text-sm">
                        Resume: <strong>+{credit.delta_resume}</strong> • Letters: <strong>+{credit.delta_letter}</strong>
                      </span>
                      <code className="text-xs text-muted-foreground">{credit.user_id.slice(0, 8)}...</code>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {new Date(credit.created_at).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Testing Guide */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Testing Guide</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="mb-2 font-semibold">1. Test with Stripe CLI</h4>
              <code className="block rounded bg-muted p-2 text-xs">
                stripe trigger checkout.session.completed
              </code>
            </div>
            <div>
              <h4 className="mb-2 font-semibold">2. Complete a Test Purchase</h4>
              <p className="text-sm text-muted-foreground">
                Use test card 4242 4242 4242 4242 with any future expiry and CVC
              </p>
            </div>
            <div>
              <h4 className="mb-2 font-semibold">3. Check Logs</h4>
              <p className="text-sm text-muted-foreground">
                View backend logs for detailed webhook processing information
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminDiagnostics;
